import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { canAccess, hasPermission } from '../../rbac';
import { propertyAPI } from '../../services/api';
import {
  apiErrorMessage,
  fullAddress,
  labelFor,
  PROPERTY_KIND_OPTIONS,
  PROPERTY_STATUS_OPTIONS,
  statusClass,
  USAGE_OPTIONS,
  VERIFICATION_OPTIONS,
} from './propertyRegistry';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import './AdminProperties.css';

const initialFilters = {
  search: '',
  property_kind: '',
  verification_status: '',
  status: '',
  ward_number: '',
};

export default function AdminPropertiesList() {
  const { tenantSlug } = useParams();
  const { user } = useAuth();
  const requestSequence = useRef(0);
  const canView = canAccess(user?.role, 'properties', user);
  const canCreate = hasPermission(user, 'properties', 'create');
  const canUpdate = hasPermission(user, 'properties', 'update');
  const [filters, setFilters] = useState(initialFilters);
  const [settledSearch, setSettledSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [properties, setProperties] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, total_pages: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [syncSuccess, setSyncSuccess] = useState('');
  const [downloading8AUid, setDownloading8AUid] = useState('');

  const handleDownloadNamuna8 = async (propertyUid) => {
    try {
      setDownloading8AUid(propertyUid);
      const res = await propertyAPI.downloadNamuna8(tenantSlug, propertyUid);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      setError(apiErrorMessage(err, 'नमुना ८ (8A) उतारा तयार करताना त्रुटी आली.'));
    } finally {
      setDownloading8AUid('');
    }
  };

  const handleSyncHouseholds = async () => {
    if (!window.confirm('ज्या नोंदणीकृत कुटुंबांसाठी अद्याप मालमत्ता नोंद झालेली नाही, त्यांच्यासाठी स्वयंचलित निवासी मालमत्ता मसुदा तयार करायचा का?')) {
      return;
    }
    try {
      setSyncing(true);
      setError('');
      setSyncSuccess('');
      const res = await propertyAPI.syncHouseholds(tenantSlug);
      setSyncSuccess(res.data?.message || 'कुटुंबांवरून मालमत्ता यशस्वीरित्या सिंक केल्या.');
      setRefreshKey(k => k + 1);
    } catch (err) {
      setError(apiErrorMessage(err, 'कुटुंब सिंक करताना त्रुटी आली.'));
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSettledSearch(filters.search.trim());
      setPage(1);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [filters.search]);

  useEffect(() => {
    if (!canView) return undefined;
    const current = ++requestSequence.current;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const params = {
          page,
          limit,
          sort_by: 'updated_at',
          sort_order: 'desc',
          search: settledSearch || undefined,
          property_kind: filters.property_kind || undefined,
          verification_status: filters.verification_status || undefined,
          status: filters.status || undefined,
          ward_number: filters.ward_number.trim() || undefined,
        };
        const response = await propertyAPI.getAll(tenantSlug, params);
        if (current !== requestSequence.current) return;
        setProperties(response.data.properties || []);
        setPagination(response.data.pagination || { total: 0, page, limit, total_pages: 0 });
      } catch (requestError) {
        if (current !== requestSequence.current) return;
        setProperties([]);
        setError(apiErrorMessage(requestError, 'मालमत्ता नोंदी लोड करता आल्या नाहीत.'));
      } finally {
        if (current === requestSequence.current) setLoading(false);
      }
    };
    load();
    return () => { requestSequence.current += 1; };
  }, [canView, filters.property_kind, filters.status, filters.verification_status, filters.ward_number, limit, page, refreshKey, settledSearch, tenantSlug]);

  const pageCount = Math.max(1, pagination.total_pages || 0);
  const hasFilters = useMemo(() => Object.values(filters).some(value => value.trim()), [filters]);
  const updateFilter = (field, value) => {
    setFilters(current => ({ ...current, [field]: value }));
    if (field !== 'search') setPage(1);
  };
  const clearFilters = () => {
    setFilters(initialFilters);
    setSettledSearch('');
    setPage(1);
  };

  if (!canView) {
    return (
      <section className="property-permission-card" role="alert">
        <h1>मालमत्ता नोंदवही</h1>
        <p>ही नोंदवही पाहण्यासाठी तुमच्याकडे परवानगी नाही.</p>
      </section>
    );
  }

  return (
    <div className="property-registry-page">
      <header className="property-page-header">
        <div>
          <p className="property-eyebrow">मालमत्ता प्रशासन</p>
          <h1>मालमत्ता नोंदवही</h1>
          <p>मालकी, वापर, पडताळणी व कर-आधारासाठी एकच अधिकृत नोंद.</p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {canCreate && (
            <button
              type="button"
              className="property-secondary-button"
              onClick={handleSyncHouseholds}
              disabled={syncing}
              title="नोंदणीकृत कुटुंबांवरून स्वयंचलित निवासी मालमत्ता मसुदा तयार करा"
            >
              {syncing ? 'सिंक होत आहे...' : '⚡ कुटुंबांवरून सिंक करा'}
            </button>
          )}
          {canCreate && (
            <Link className="property-primary-button" to={`/admin/${tenantSlug}/properties/new`}>
              <span aria-hidden="true">＋</span> मालमत्ता जोडा
            </Link>
          )}
        </div>
      </header>

      {syncSuccess && (
        <div className="property-alert success" style={{ marginBottom: '1.25rem', padding: '0.75rem 1rem', borderRadius: '6px', backgroundColor: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0' }}>
          {syncSuccess}
        </div>
      )}

      <section className="property-filter-card" aria-label="मालमत्ता गाळणी">
        <div className="property-search-field">
          <label htmlFor="property-search">शोधा</label>
          <input
            id="property-search"
            type="search"
            value={filters.search}
            onChange={event => updateFilter('search', event.target.value)}
            placeholder="नाव, मिळकत क्रमांक, पत्ता, मालक किंवा फोन"
          />
        </div>
        <label>
          प्रकार
          <select value={filters.property_kind} onChange={event => updateFilter('property_kind', event.target.value)}>
            <option value="">सर्व</option>
            {PROPERTY_KIND_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <label>
          पडताळणी
          <select value={filters.verification_status} onChange={event => updateFilter('verification_status', event.target.value)}>
            <option value="">सर्व</option>
            {VERIFICATION_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <label>
          नोंद स्थिती
          <select value={filters.status} onChange={event => updateFilter('status', event.target.value)}>
            <option value="">संग्रहित वगळून</option>
            {PROPERTY_STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <label>
          प्रभाग
          <input value={filters.ward_number} onChange={event => updateFilter('ward_number', event.target.value)} placeholder="उदा. 3" />
        </label>
        {hasFilters && <button type="button" className="property-text-button" onClick={clearFilters}>गाळणी साफ करा</button>}
      </section>

      <section className="property-list-card" aria-busy={loading}>
        <div className="property-list-meta">
          <p><strong>{pagination.total || 0}</strong> नोंदी</p>
          {loading && <span className="property-loading-label" role="status">माहिती लोड होत आहे…</span>}
        </div>

        {error && (
          <div className="property-alert error" role="alert">
            <p>{error}</p>
            <button type="button" onClick={() => setRefreshKey(value => value + 1)}>पुन्हा प्रयत्न करा</button>
          </div>
        )}

        {loading && properties.length === 0 && (
          <LoadingSpinner fullPage message="मालमत्ता माहिती लोड होत आहे…" />
        )}

        {!loading && !error && properties.length === 0 && (
          <div className="property-empty-state">
            <span aria-hidden="true">⌂</span>
            <h2>मालमत्ता सापडली नाही</h2>
            <p>{hasFilters ? 'फिल्टर बदला किंवा साफ करा.' : 'पहिली मालमत्ता नोंदवून नोंदवही सुरू करा.'}</p>
            {hasFilters && <button type="button" className="property-secondary-button" onClick={clearFilters}>गाळणी साफ करा</button>}
          </div>
        )}

        {properties.length > 0 && (
          <div className={`property-table-wrap ${loading ? 'is-loading' : ''}`}>
            <table className="property-table">
              <thead>
                <tr>
                  <th scope="col">मालमत्ता</th>
                  <th scope="col">प्राथमिक मालक</th>
                  <th scope="col">वर्गीकरण</th>
                  <th scope="col">प्रभाग</th>
                  <th scope="col">स्थिती</th>
                  <th scope="col"><span className="sr-only">कृती</span></th>
                </tr>
              </thead>
              <tbody>
                {properties.map(property => (
                  <tr key={property.property_uid}>
                    <td data-label="मालमत्ता">
                      <div className="property-identity-cell">
                        <span aria-hidden="true">{property.property_kind === 'parcel' ? '▱' : property.property_kind === 'unit' ? '▦' : '⌂'}</span>
                        <div>
                          <Link to={`/admin/${tenantSlug}/properties/${property.property_uid}`}>
                            {property.property_name}
                          </Link>
                          <small>{property.assessment_number} · {fullAddress(property) || property.property_uid}</small>
                        </div>
                      </div>
                    </td>
                    <td data-label="मालक">
                      <strong>{property.primary_owner?.display_name || property.primary_owner?.legal_name || '—'}</strong>
                      {property.primary_owner?.primary_phone && <small className="property-cell-subtext">{property.primary_owner.primary_phone}</small>}
                    </td>
                    <td data-label="वर्गीकरण">
                      <span>{labelFor(PROPERTY_KIND_OPTIONS, property.property_kind)}</span>
                      <small className="property-cell-subtext">{labelFor(USAGE_OPTIONS, property.usage_type)}</small>
                    </td>
                    <td data-label="प्रभाग">{property.ward_number || '—'}</td>
                    <td data-label="स्थिती">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', alignItems: 'flex-start' }}>
                        <span className={`property-status ${statusClass(property.verification_status)}`}>
                          {labelFor(VERIFICATION_OPTIONS, property.verification_status)}
                        </span>
                        {property.metadata?.auto_generated && (
                          <span
                            className="property-status"
                            style={{ backgroundColor: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d', fontSize: '0.72rem', fontWeight: 600, padding: '2px 6px', borderRadius: '4px', whiteSpace: 'nowrap' }}
                            title="कुटुंबावरून स्वयंचलित मसुदा: क्षेत्रफळ, मूल्यांकन व मिळकत क्रमांक भरणे बाकी आहे"
                          >
                            ⚠ अपूर्ण तपशील
                          </span>
                        )}
                        {property.status !== 'active' && (
                          <small className="property-cell-subtext">{labelFor(PROPERTY_STATUS_OPTIONS, property.status)}</small>
                        )}
                      </div>
                    </td>
                    <td data-label="कृती">
                      <div className="property-row-actions">
                        <Link className="property-table-link" to={`/admin/${tenantSlug}/properties/${property.property_uid}`}>तपशील</Link>
                        {canUpdate && property.status !== 'archived' && (
                          <Link className="property-table-link" to={`/admin/${tenantSlug}/properties/${property.property_uid}/edit`}>संपादन</Link>
                        )}
                        <button
                          type="button"
                          className="property-table-link"
                          onClick={() => handleDownloadNamuna8(property.property_uid)}
                          disabled={downloading8AUid === property.property_uid}
                          title="नमुना ८ (8A) कर आकारणी नोंदवही उतारा डाउनलोड / प्रिंट करा"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, font: 'inherit', color: '#1e3a8a', fontWeight: 600 }}
                        >
                          {downloading8AUid === property.property_uid ? '...' : '📄 नमुना ८'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <footer className="property-pagination" aria-label="मालमत्ता पृष्ठक्रम">
          <label>
            प्रति पृष्ठ
            <select value={limit} onChange={event => { setLimit(Number(event.target.value)); setPage(1); }}>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </label>
          <div>
            <button type="button" disabled={loading || page <= 1} onClick={() => setPage(current => current - 1)}>← मागील</button>
            <span>पृष्ठ <strong>{page}</strong> / {pageCount}</span>
            <button type="button" disabled={loading || page >= pageCount} onClick={() => setPage(current => current + 1)}>पुढील →</button>
          </div>
        </footer>
      </section>
    </div>
  );
}
